import React, { createContext, useMemo } from "react";
import { Box, Paper } from "@mui/material";
import TableLoading from "../../../components/TableLoading/TableLoading";
import { GridHeader } from "./GridHeader/GridHeader";
import { MaterialSection } from "./MaterialSection/MaterialSection";
import { BlockSection } from "./BlockSection/BlockSection";
import { getSchedulerConfig, getGridTemplate } from "./configs";
import { groupMaterials, sortBlocks, TYPE_LABELS } from "./PlanningGrid.utils";

export const PlanningGridContext = createContext({});
export const SelectionContext = createContext({});

export function PlanningGrid({
  planning,
  contentId,
  handleEdit,
  handleDeleteVersion,
  handleDeleteChapter,
  refresh,
  schedulerType,
  handleDeleteAttachment,
  handleDeleteAddendum,
  attachmentActions,
  handleEditFile,
  handleOpenReleaseTexts,
  selectedFiles,
  setSelectedFiles,
  isSelectionMode,
  selectedFileIds,
  openIds,
  toggleOpenId,
  type,
}) {
  const schedulerConfig = useMemo(
    () => getSchedulerConfig(schedulerType),
    [schedulerType]
  );
  const grid = useMemo(
    () => getGridTemplate(isSelectionMode),
    [isSelectionMode]
  );

  const { materials = [], blocks = [] } = planning.data || {};

  const groupedMaterials = useMemo(
    () => groupMaterials(materials),
    [materials]
  );

  const sortedBlocks = useMemo(() => sortBlocks(blocks), [blocks]);

  const contextValue = useMemo(
    () => ({
      ...schedulerConfig,
      grid,
      isSelectionMode,
      openIds,
      toggleOpenId,
    }),
    [schedulerConfig, grid, isSelectionMode, openIds, toggleOpenId]
  );

  const selectionContextValue = useMemo(
    () => ({ selectedFiles, selectedFileIds, setSelectedFiles }),
    [selectedFiles, selectedFileIds, setSelectedFiles]
  );

  if (planning.loading) return <TableLoading />;

  return (
    <PlanningGridContext.Provider value={contextValue}>
      <SelectionContext.Provider value={selectionContextValue}>
        <Box
          sx={{
            margin: "24px 0 0px 0",
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              pt: 0,
              pb: 0,
              bgcolor: "transparent",
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                margin: "0 0 24px 0",
              }}
            >
              <GridHeader />

              {groupedMaterials.map((group, index) => {
                const typeId = Object.keys(group).find((k) => k !== "name");
                const data = group[typeId];
                const name = TYPE_LABELS[Number(typeId)] ?? group.name;

                return (
                  <MaterialSection
                    key={`${typeId}-${name}`}
                    id={`${typeId}-${name}`}
                    data={data}
                    name={name}
                    contentId={contentId}
                    handleEdit={handleEdit}
                    handleDeleteVersion={handleDeleteVersion}
                    handleEditFile={handleEditFile}
                    handleDelete={handleDeleteAttachment}
                    handleDeleteAddendum={handleDeleteAddendum}
                    attachmentActions={attachmentActions}
                    handleOpenReleaseTexts={handleOpenReleaseTexts}
                  />
                );
              })}

              {sortedBlocks.map((bloco, index) => (
                <BlockSection
                  key={bloco.id}
                  bloco={bloco}
                  contentId={contentId}
                  isFirstBlock={!index}
                  isLastBlock={index === sortedBlocks.length - 1}
                  handleEdit={handleEdit}
                  handleDeleteChapter={handleDeleteChapter}
                  handleEditFile={handleEditFile}
                  previousBlockId={index ? sortedBlocks[index - 1].id : null}
                  nextBlockId={
                    index !== sortedBlocks.length - 1
                      ? sortedBlocks[index + 1].id
                      : null
                  }
                  refresh={refresh}
                  handleDeleteAttachment={handleDeleteAttachment}
                  handleDeleteAddendum={handleDeleteAddendum}
                  attachmentActions={attachmentActions}
                  handleOpenReleaseTexts={handleOpenReleaseTexts}
                />
              ))}
            </Box>
          </Paper>
        </Box>
      </SelectionContext.Provider>
    </PlanningGridContext.Provider>
  );
}
